import React from 'react';

/**
 * Catches errors that escape the top-level Suspense boundary — most notably
 * a chunk that keeps failing after lazyWithRetry has already tried its
 * one-shot reload. In that scenario the sessionStorage guard prevents
 * another auto-reload (to avoid infinite loops), so the error propagates
 * here and we show a "reload the page" fallback instead of a blank screen.
 */
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('RouteErrorBoundary caught:', error, info?.componentStack);
  }

  handleReload = () => {
    try { sessionStorage.removeItem('epf.chunk_reload_attempted'); } catch {}
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-ep-background px-6" data-testid="route-error-boundary">
        <div className="max-w-md text-center">
          <h1 className="font-heading text-3xl text-ep-text mb-3">Something didn't load correctly</h1>
          <p className="text-ep-text-muted mb-6">
            The page couldn't finish loading — usually because the app was updated in the background.
            Reloading the page should fix it.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center justify-center h-11 px-6 rounded-md bg-ep-primary text-white font-medium hover:bg-ep-primary-dark"
            data-testid="route-error-reload-btn"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
